import { Message } from '@circuitry-ai/doc-data';

export const buildMessageMetadataMap = (
  metaData: Partial<Message>['metaData']
): Record<string, unknown> => {
  if (Array.isArray(metaData)) {
    return metaData.reduce((acc: Record<string, unknown>, obj: Record<string, unknown>) => {
      if (obj && typeof obj === 'object') {
        Object.entries(obj).forEach(([key, value]) => {
          if (key) acc[key] = value;
        });
      }
      return acc;
    }, {});
  }
  if (metaData && typeof metaData === 'object') {
    return { ...(metaData as Record<string, unknown>) };
  }
  return {};
};

export const isImagePresentFlag = (value: unknown): boolean => {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
};

export const hasImagePresentInMetadata = (
  metaData: Partial<Message>['metaData']
): boolean => {
  const metadataMap = buildMessageMetadataMap(metaData);

  // If image_present is explicitly true, show images
  if ('image_present' in metadataMap && isImagePresentFlag(metadataMap.image_present)) {
    return true;
  }
  
  // If image_present is explicitly false/0/"false", never show images
  if ('image_present' in metadataMap && metadataMap.image_present !== true) {
    return false;
  }

  // If image_present is absent, fall back to checking whether any source has an img_path
  const hasImgPath = (metaData?.[1]?.source_ids ?? []).slice(0, 5).some(
    (source: { img_path?: string }) => source?.img_path && String(source.img_path).trim() !== ''
  );
  return hasImgPath;
};

export const extractImagePathsFromMetadata = (
  metaData: Partial<Message>['metaData']
): string[] => {
  return (
    Array.from(
      new Set(
        metaData?.[1]?.source_ids
          ?.slice(0, 5)
          ?.flatMap((source: { img_path: string }) =>
            source.img_path ? [source.img_path] : []
          )
      )
    ) || []
  );
};
